export enum PowerMeterFormDeviceType {
  InternalRootPowerMeter = 'internal-root-power-meter',
  ExternalRootPowerMeter = 'external-root-power-meter',
  ProductionMeter = 'production-meter',
  ConsumptionMeter = 'consumption-meter',
  BidirectionalMeter = 'bidirectional-meter',
  ThirdPartyMeter = 'third-party-meter',
  ControllerBypass = 'controller-bypass',
  ProductionMeterWithoutStorage = 'production-meter-without-storage',
}

export interface PowerMeterForm {
  index?: number
  name: string
  address?: number
  /** UI-only field, gates connection direction and current transformer settings */
  deviceType?: PowerMeterFormDeviceType
  wiringMode: 'Normal' | 'Inverted'
  useCT: boolean
  ratioCT: string
  serialNumber?: string
  powerMeterIpAddress?: string
}

export type PlannedPowerMeterType =
  | 'InternalRoot'
  | 'ExternalRoot'
  | 'Production'
  | 'Consumption'
  | 'Bidirectional'
  | 'ThirdParty'
  | 'ControllerBypass'
  | 'ProductionWithoutStorage'

export type PlannedPowerMeterDeviceType = PlannedPowerMeterType | 'HVAC' | 'PVExtern' | 'House' | 'Evse' | ''

export interface PlannedPowerMeterModel {
  id: number
  deviceLinkId: number
  deviceName: string
  media: 'ModbusRTU' | 'ModbusTCP'
  mediaParameters?: {
    address?: number | null
    serialNumber?: string | null
    communicationInterfaceAddress?: string | null
  }
  deviceType: PlannedPowerMeterDeviceType
  typeParameters: object
  wiringMode: 'Normal' | 'Inverted'
}

export type PlannedPowerMeterCreatePayload = {
  number?: number
  deviceName: string
  media: 'ModbusTCP'
  mediaParameters?: { serialNumber?: string | null }
  deviceType: PlannedPowerMeterType
  typeParameters: Record<string, never>
  wiringMode: 'Normal' | 'Inverted'
}

export type PlannedPowerMeterSavePayload = {
  number?: number
  deviceName: string
  media: 'ModbusTCP'
  mediaParameters?: { serialNumber?: string | null }
  deviceType: PlannedPowerMeterDeviceType
  typeParameters: object
  wiringMode: 'Normal' | 'Inverted'
}

/**
 * The model options exposed in the wallbox configuration UI. Each maps to a concrete
 * media/wbType/deviceReference combination understood by the backend, see
 * src/Libs/installations/utils/wallbox-model-type.ts
 */
export type WallboxModelType = 'efy' | 'edsn' | 'multi-connect' | 'multi-connect-2'

export interface WallboxForm {
  name: string
  modelType: WallboxModelType
}

export interface PlannedWallboxModel {
  id: number
  deviceLinkId: number
  deviceName: string
  modelType: WallboxModelType | null
  /**
   * Opaque passthrough of the backend `parameters`/`evseSubTypeParameters` that are not managed by
   * the simple wallbox configuration UI (e.g. OCPP settings, sun mode). Preserved as-is on update so
   * saving the name/model type doesn't reset values configured elsewhere.
   */
  parameters: object
  evseSubTypeParameters: object
}

export interface PlannedWallboxesResult {
  wallboxes: PlannedWallboxModel[]
  /**
   * The backend limits the total number of "controlled" sub-devices (of any type, not just
   * wallboxes) per device-link, see MAX_CONTROLLED_SUB_DEVICES_PER_DEVICE_LINK.
   */
  totalControlledSubDeviceCount: number
}
